import type { ReactNode } from 'react'
import type { AsyncState } from './Async.js'
import { Async } from './Async.js'
import { Pager, TableSkeleton } from './Pager.js'
import type { MinWidth } from './Table.js'
import { Table } from './Table.js'

/**
 * A screen that is a title and a paged table.
 *
 * Five of them exist and about ten more are coming. Written per screen, each
 * one repeats the same four decisions — where the heading sits, that loading
 * shows a skeleton rather than a spinner, that a failure offers a retry, and
 * that an empty result gets its own words rather than an empty table. jscpd
 * found the third copy the moment it was written, which is exactly what it is
 * for.
 *
 * What stays per screen is what is actually different: the columns, how a row
 * renders, and what to say when there is nothing. Those arrive as props rather
 * than as configuration, so a screen still reads as a screen.
 */

export interface PagedResult<T> {
  readonly rows: readonly T[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

export function PagedScreen<T>({
  title,
  intro,
  state,
  onRetry,
  onPage,
  before,
  head,
  minWidth,
  empty,
  row,
}: {
  readonly title: string
  readonly intro: string
  readonly state: AsyncState<PagedResult<T>>
  readonly onRetry: () => void
  readonly onPage: (page: number) => void
  /**
   * Rendered between the heading and the table.
   *
   * For a screen whose log is not the first thing somebody needs — the sending
   * monitor leads with what is stuck, because that is what asks for a person.
   */
  readonly before?: ReactNode
  readonly head: readonly string[]
  readonly minWidth?: MinWidth
  /** Its own words. "No rows" tells someone nothing they can act on. */
  readonly empty: ReactNode
  readonly row: (item: T) => ReactNode
}) {
  return (
    <>
      <header className="mb-6">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">{title}</h1>
        <p className="mt-1 text-[13px] text-t2">{intro}</p>
      </header>

      {before}

      <PagedTable
        state={state}
        onRetry={onRetry}
        onPage={onPage}
        head={head}
        {...(minWidth === undefined ? {} : { minWidth })}
        empty={empty}
        row={row}
      />
    </>
  )
}

/**
 * The table and its pager, without the heading above them.
 *
 * A paged list is not always a whole screen: the campaign report puts one under
 * its figures, inside a section with its own title. Written a second time for
 * that, it would be a second copy of the four decisions `PagedScreen` exists to
 * hold — and the copy is where one of them would go missing.
 */
export function PagedTable<T>({
  state,
  onRetry,
  onPage,
  head,
  minWidth,
  empty,
  row,
}: {
  readonly state: AsyncState<PagedResult<T>>
  readonly onRetry: () => void
  readonly onPage: (page: number) => void
  readonly head: readonly string[]
  readonly minWidth?: MinWidth
  /** Its own words. "No rows" tells someone nothing they can act on. */
  readonly empty: ReactNode
  readonly row: (item: T) => ReactNode
}) {
  return (
    <Async state={state} skeleton={<TableSkeleton />} onRetry={onRetry}>
      {(result) =>
        result.rows.length === 0 ? (
          empty
        ) : (
          <>
            <Table head={head} {...(minWidth === undefined ? {} : { minWidth })}>
              {result.rows.map(row)}
            </Table>
            <Pager
              page={result.page}
              pageSize={result.pageSize}
              total={result.total}
              onPage={onPage}
            />
          </>
        )
      }
    </Async>
  )
}

/**
 * What a screen says when it has nothing to show.
 *
 * Always two sentences: what is not here, and why that might be — because
 * "nothing is suppressed" reads as reassuring and "suppression is switched off"
 * reads as alarming, and only one of them is true.
 */
export function EmptyCard({
  heading,
  children,
}: {
  readonly heading: string
  readonly children: ReactNode
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-10 text-center">
      <EmptyNote heading={heading}>{children}</EmptyNote>
    </div>
  )
}

/**
 * The words of an empty listing, without the card around them.
 *
 * A board says its nothing *inside* its own table, so that the column headings
 * — and the `⋯` that manages a column — survive having no rows. A card there
 * would be a card inside a card, so the two share the words and differ only in
 * what is drawn around them.
 */
export function EmptyNote({
  heading,
  children,
}: {
  readonly heading: string
  readonly children: ReactNode
}) {
  return (
    <>
      <h2 className="font-bold text-[15px] text-t1">{heading}</h2>
      <p className="mx-auto mt-2 max-w-md text-[13px] text-t2 leading-relaxed">{children}</p>
    </>
  )
}

/**
 * The same table, without the paging.
 *
 * A list that is never long enough to page — the domains you send from, your
 * API keys, the people in your organisation — still owes a reader the same four
 * things: a skeleton rather than a spinner, a retry on failure, its own words
 * when there is nothing, and never an empty table standing in for a request
 * that did not come back. Written per screen those four are four copies, which
 * is how the third one ends up quietly rendering an empty list over an error.
 *
 * Beside `PagedScreen` rather than inside it. The difference is not a flag: a
 * paged screen owns a page number and a total, and a list that cannot be paged
 * has neither to give it.
 */
export function ListScreen<T>({
  state,
  onRetry,
  head,
  minWidth,
  empty,
  row,
}: {
  readonly state: AsyncState<readonly T[]>
  readonly onRetry: () => void
  readonly head: readonly string[]
  readonly minWidth?: MinWidth
  /** Its own words. "No rows" tells someone nothing they can act on. */
  readonly empty: ReactNode
  readonly row: (item: T) => ReactNode
}) {
  return (
    <Async state={state} skeleton={<TableSkeleton />} onRetry={onRetry}>
      {(rows) =>
        rows.length === 0 ? (
          empty
        ) : (
          <Table head={head} {...(minWidth === undefined ? {} : { minWidth })}>
            {rows.map(row)}
          </Table>
        )
      }
    </Async>
  )
}
